import { useMemo, FC, useState, type MouseEvent } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import { IconDots, IconEdit } from "@tabler/icons-react";
import { PromptResponse } from "./types";
import DeletePromptDialog from "./DeletePromptDialog";
import { useUsersWithServiceUser } from "@/helpers/hooks";
import { useAppState } from "@/modules/app/useAppContext";
import { usePromptLibrary } from "./PromptLibraryContext";
import { isServiceUser } from "@/services/auth";

interface PromptActionsMenuProps {
  prompt: PromptResponse;
}

const PromptActionsMenu: FC<PromptActionsMenuProps> = ({ prompt }) => {
  const [open, setOpen] = useState(false);
  const { loggedinUser } = useAppState();
  const { users } = useUsersWithServiceUser();
  const { openEditPrompt } = usePromptLibrary();

  const handleEdit = (e: MouseEvent) => {
    e.stopPropagation();
    openEditPrompt(prompt);
  };

  // Check if current user can edit/delete this prompt.
  // Service users can edit public prompts and prompts they created.
  // The service user is excluded from the regular users list, so a missing creator means
  // the prompt was created by the service user — treat it the same as a public prompt.
  const canEdit = useMemo(() => {
    if (prompt.is_public) {
      return isServiceUser();
    }
    const createdBy = users?.find(user => user.id === prompt.created_by);
    if (!createdBy) {
      return isServiceUser();
    }
    return createdBy.id === loggedinUser?.id;
  }, [loggedinUser?.id, prompt.created_by, prompt.is_public, users]);

  const getTooltipTitle = () => {
    if (!canEdit) {
      const createdBy = users?.find(user => user.id === prompt.created_by);
      if (prompt.is_public || !createdBy) {
        return "Cannot change default prompts";
      }
      return "You do not have permission to edit this prompt";
    }
    return "Actions";
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <Tooltip title={getTooltipTitle()}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={e => e.stopPropagation()}
            className="al-h-8 al-w-8 al-p-0"
            disabled={!canEdit}
            aria-label="Prompt actions"
          >
            <IconDots className="al-h-4 al-w-4" />
          </Button>
        </DropdownMenuTrigger>
      </Tooltip>

      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleEdit}>
          <IconEdit className="al-h-4 al-w-4" />
          Edit
        </DropdownMenuItem>
        <DeletePromptDialog prompt={prompt} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default PromptActionsMenu;
